import { useState, useEffect, useCallback } from "react";
import {
  FileText, Plus, Trash2, Download, CheckCircle, Clock, XCircle,
  X, ChevronRight, Euro, AlertCircle, Edit3
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtEur(v) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v || 0);
}
function fmtDatum(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

const STATUS_CONFIG = {
  open:      { label: "Open",        icon: <Clock size={11} />,        cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  betaald:   { label: "Betaald",     icon: <CheckCircle size={11} />, cls: "bg-green-500/10 text-green-400 border-green-500/20" },
  annulering:{ label: "Annulering",  icon: <XCircle size={11} />,     cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

// ── Factuur Modal ─────────────────────────────────────────────────────────────
function FactuurModal({ factuur, onClose, onSave, token }) {
  const isEdit = Boolean(factuur?.id);
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    klant_naam: factuur?.klant?.naam || "",
    klant_email: factuur?.klant?.email || "",
    klant_adres: factuur?.klant?.adres || "",
    klant_btw: factuur?.klant?.btw_nummer || "",
    datum: factuur?.datum || today,
    vervaldatum: factuur?.vervaldatum || in30,
    btw_percentage: factuur?.btw_percentage ?? 21,
    notities: factuur?.notities || "",
    regels: factuur?.regels || [{ omschrijving: "", aantal: 1, prijs_per_stuk: 0 }],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setRegel = (i, k, v) => setForm(f => ({ ...f, regels: f.regels.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addRegel = () => setForm(f => ({ ...f, regels: [...f.regels, { omschrijving: "", aantal: 1, prijs_per_stuk: 0 }] }));
  const removeRegel = (i) => setForm(f => ({ ...f, regels: f.regels.filter((_, idx) => idx !== i) }));

  const excl = form.regels.reduce((s, r) => s + r.aantal * r.prijs_per_stuk, 0);
  const btw = excl * form.btw_percentage / 100;
  const incl = excl + btw;

  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600";
  const labelCls = "text-zinc-500 text-xs font-semibold uppercase tracking-wide mb-1 block";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.klant_naam.trim()) { setError("Klantnaam is verplicht"); return; }
    if (!form.regels.some(r => r.omschrijving.trim())) { setError("Voeg minimaal één factuurregel in"); return; }
    setSaving(true); setError("");
    try {
      const url = isEdit ? `${API}/api/company/facturen/${factuur.id}` : `${API}/api/company/facturen`;
      const r = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, btw_percentage: Number(form.btw_percentage) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      onSave();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <h3 className="text-white font-bold">{isEdit ? "Factuur bewerken" : "Nieuwe factuur"}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Klantgegevens */}
          <div>
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-3">Klantgegevens</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Klantnaam *</label>
                <input value={form.klant_naam} onChange={e => set("klant_naam", e.target.value)} placeholder="Naam klant of bedrijf" className={inputCls} data-testid="klant-naam-input" />
              </div>
              <div>
                <label className={labelCls}>E-mailadres</label>
                <input type="email" value={form.klant_email} onChange={e => set("klant_email", e.target.value)} placeholder="klant@email.nl" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>BTW-nummer</label>
                <input value={form.klant_btw} onChange={e => set("klant_btw", e.target.value)} placeholder="NL123456789B01" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Adres</label>
                <input value={form.klant_adres} onChange={e => set("klant_adres", e.target.value)} placeholder="Straat 1, 1234 AB Amsterdam" className={inputCls} />
              </div>
            </div>
          </div>

          {/* Datums + BTW */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Factuurdatum</label>
              <input type="date" value={form.datum} onChange={e => set("datum", e.target.value)} className={inputCls} data-testid="factuur-datum-input" />
            </div>
            <div>
              <label className={labelCls}>Vervaldatum</label>
              <input type="date" value={form.vervaldatum} onChange={e => set("vervaldatum", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>BTW %</label>
              <input type="number" min={0} max={100} value={form.btw_percentage} onChange={e => set("btw_percentage", e.target.value)} className={inputCls} data-testid="btw-percentage-input" />
            </div>
          </div>

          {/* Factuurregels */}
          <div>
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-3">Factuurregels</p>
            <div className="space-y-2">
              {form.regels.map((regel, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input value={regel.omschrijving} onChange={e => setRegel(i, "omschrijving", e.target.value)}
                      placeholder="Omschrijving dienst of product"
                      data-testid={`regel-omschrijving-${i}`}
                      className={inputCls} />
                  </div>
                  <div className="w-16">
                    <input type="number" min={1} value={regel.aantal} onChange={e => setRegel(i, "aantal", Number(e.target.value))}
                      placeholder="Aantal" className={inputCls} />
                  </div>
                  <div className="w-24">
                    <input type="number" min={0} step={0.01} value={regel.prijs_per_stuk} onChange={e => setRegel(i, "prijs_per_stuk", parseFloat(e.target.value) || 0)}
                      placeholder="€ prijs"
                      data-testid={`regel-prijs-${i}`}
                      className={inputCls} />
                  </div>
                  <button type="button" onClick={() => removeRegel(i)} disabled={form.regels.length === 1}
                    className="p-2 text-zinc-600 hover:text-red-400 disabled:opacity-30 mt-0.5">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addRegel} data-testid="add-regel-btn"
              className="mt-2 flex items-center gap-2 text-[#C9A84C] text-sm font-semibold hover:opacity-80">
              <Plus size={14} /> Regel toevoegen
            </button>
          </div>

          {/* Totalen preview */}
          <div className="bg-[#1a1a1a] rounded-xl p-4 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Subtotaal (excl. BTW)</span>
              <span className="text-zinc-300">{fmtEur(excl)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">BTW ({form.btw_percentage}%)</span>
              <span className="text-zinc-300">{fmtEur(btw)}</span>
            </div>
            <div className="flex justify-between font-bold border-t border-white/10 pt-1.5">
              <span className="text-white">Totaal</span>
              <span className="text-[#C9A84C] text-lg">{fmtEur(incl)}</span>
            </div>
          </div>

          {/* Notities */}
          <div>
            <label className={labelCls}>Notities (optioneel)</label>
            <textarea value={form.notities} onChange={e => set("notities", e.target.value)} rows={2}
              placeholder="Extra toelichting op de factuur..." className={`${inputCls} resize-none`} />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="save-factuur-btn"
              className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black text-sm font-bold disabled:opacity-40">
              {saving ? "Opslaan..." : isEdit ? "Bijwerken" : "Factuur aanmaken"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Hoofd component ───────────────────────────────────────────────────────────
export default function BedrijfFacturenTab({ token }) {
  const [facturen, setFacturen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editFactuur, setEditFactuur] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/company/facturen`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setFacturen((await r.json()).facturen || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (factuur, status) => {
    try {
      const r = await fetch(`${API}/api/company/facturen/${factuur.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      setFacturen(prev => prev.map(f => f.id === factuur.id ? { ...f, status } : f));
      showToast("Status bijgewerkt");
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Factuur definitief verwijderen?")) return;
    try {
      const r = await fetch(`${API}/api/company/facturen/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      showToast("Factuur verwijderd");
      load();
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleDownloadPdf = async (factuur) => {
    setDownloading(factuur.id);
    try {
      const r = await fetch(`${API}/api/company/facturen/${factuur.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error("PDF genereren mislukt");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factuur-${factuur.factuur_nr || factuur.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { showToast(e.message, "error"); }
    setDownloading(null);
  };

  const totaalOpen = facturen.filter(f => f.status === "open").reduce((s, f) => s + (f.totaal_incl_btw || 0), 0);
  const totaalBetaald = facturen.filter(f => f.status === "betaald").reduce((s, f) => s + (f.totaal_incl_btw || 0), 0);

  return (
    <div className="max-w-4xl" data-testid="bedrijf-facturen-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Factuurbeheer</p>
          <h2 className="text-xl font-bold text-white">Facturen</h2>
          <p className="text-zinc-500 text-sm mt-1">Maak professionele PDF-facturen voor je klanten</p>
        </div>
        <button onClick={() => { setEditFactuur(null); setShowModal(true); }}
          data-testid="nieuwe-factuur-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-black text-sm font-bold"
          style={{ background: "#C9A84C" }}>
          <Plus size={14} /> Nieuwe factuur
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: "Totaal facturen", val: facturen.length, icon: <FileText size={16} /> },
          { label: "Openstaand", val: fmtEur(totaalOpen), icon: <Clock size={16} />, highlight: totaalOpen > 0 },
          { label: "Ontvangen", val: fmtEur(totaalBetaald), icon: <CheckCircle size={16} /> },
        ].map(s => (
          <div key={s.label} className="bg-[#111] border border-white/8 rounded-2xl p-4 text-center">
            <div className={`flex justify-center mb-2 ${s.highlight ? "text-yellow-400" : "text-[#C9A84C]"}`}>{s.icon}</div>
            <p className="text-white font-black text-lg">{s.val}</p>
            <p className="text-zinc-600 text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Facturen tabel */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : facturen.length === 0 ? (
        <div className="bg-[#111] border border-white/8 rounded-2xl p-10 text-center">
          <FileText size={28} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 font-semibold mb-1">Nog geen facturen</p>
          <p className="text-zinc-600 text-sm mb-4">Maak je eerste professionele factuur aan inclusief BTW-berekening en PDF download.</p>
        </div>
      ) : (
        <div className="bg-[#111] border border-white/8 rounded-2xl overflow-hidden">
          <table className="w-full" data-testid="facturen-tabel">
            <thead>
              <tr className="border-b border-white/8 text-left">
                {["Factuur", "Klant", "Datum", "Bedrag", "Status", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-zinc-500 text-xs font-semibold uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {facturen.map(f => {
                const sc = STATUS_CONFIG[f.status] || STATUS_CONFIG.open;
                return (
                  <tr key={f.id} className="border-b border-white/5 hover:bg-white/2 transition-colors" data-testid={`factuur-row-${f.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-bold">{f.factuur_nr}</p>
                    </td>
                    <td className="px-4 py-3 max-w-[180px]">
                      <p className="text-zinc-300 text-sm truncate">{f.klant?.naam || "—"}</p>
                      <p className="text-zinc-600 text-xs truncate">{f.klant?.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-zinc-400 text-sm">{fmtDatum(f.datum)}</p>
                      {f.vervaldatum && <p className="text-zinc-600 text-xs">Vervalt {fmtDatum(f.vervaldatum)}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white font-bold text-sm">{fmtEur(f.totaal_incl_btw)}</p>
                      <p className="text-zinc-600 text-xs">incl. {f.btw_percentage}% BTW</p>
                    </td>
                    <td className="px-4 py-3">
                      <select value={f.status} onChange={e => handleStatusChange(f, e.target.value)}
                        data-testid={`status-select-${f.id}`}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-lg border cursor-pointer bg-transparent ${sc.cls}`}>
                        <option value="open">Open</option>
                        <option value="betaald">Betaald</option>
                        <option value="annulering">Annulering</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button onClick={() => handleDownloadPdf(f)} disabled={downloading === f.id}
                          data-testid={`download-pdf-${f.id}`}
                          title="PDF downloaden"
                          className="p-1.5 rounded-lg text-zinc-500 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all disabled:opacity-40">
                          <Download size={13} />
                        </button>
                        <button onClick={() => { setEditFactuur(f); setShowModal(true); }}
                          data-testid={`edit-factuur-${f.id}`}
                          title="Bewerken"
                          className="p-1.5 rounded-lg text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 transition-all">
                          <Edit3 size={13} />
                        </button>
                        <button onClick={() => handleDelete(f.id)}
                          data-testid={`delete-factuur-${f.id}`}
                          title="Verwijderen"
                          className="p-1.5 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <FactuurModal
          factuur={editFactuur}
          token={token}
          onClose={() => { setShowModal(false); setEditFactuur(null); }}
          onSave={() => { setShowModal(false); setEditFactuur(null); load(); showToast(editFactuur ? "Factuur bijgewerkt" : "Factuur aangemaakt"); }}
        />
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold shadow-2xl ${toast.type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
          {toast.type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
